import { memo, useMemo } from 'react';
import { Button, Tag, Tooltip } from 'antd';
import styled from '@emotion/styled';
import { CopyFilled, ExclamationCircleOutlined, RightOutlined } from '@ant-design/icons';

type ExtraTagProps = {
  message: Conversation.Message;
  float: 'left' | 'right';
};

const tradeStatusMap: any = {
  PRE_ORDER: '无订单',
  WAITING_PAY: '待付款',
  WAITING_DELIVERY: '待发货',
  WAITING_SIGN: '待签收',
  TRADE_SUCCESS: '交易成功',
  TRACE_PACKAGE_LOSS: '物流包裹丢失',
  TRACE_PACKAGE_BROKE: '物流包裹损坏',
  IN_CANCEL: '买家申请取消订单',
  CANCELED: '已取消',
  UNKNOWN_ORDER_STATUS: '未知',
};

const traceStatusMap: any = {
  WAITING_COLLECT: '待揽收',
  WAITING_SEND: '待派送',
  WAITING_SIGN: '代签收',
  SIGNED: '已签收',
  SEND_FAIL: '派送失败，退回中',
  RETURN_FAIL: '包裹退回失败',
  RETURN_SUCCESS: '包裹退回成功',
};

const refundStatusMap: any = {
  NO_REFUND: '无退换',
  YES_REFUND: '有退换',
  RETURNED: '已退回',
  RETURNING: '退货中',
  RETURN_FAIL: '退货失败',
  SUCCESS: '退换成功',
  RETURN_PACKAGE_BROKE: '退货包裹损坏',
};

const serviceModeMap: any = {
  NORMAL: '辅助模式',
  NIGHT_WATCH: '值守模式',
};

const TagWrapper = styled.div<{ float: 'left' | 'right' }>`
  display: flex;
  justify-content: ${({ float }) => (float === 'left' ? 'flex-start' : 'flex-end')};
  align-items: center;
  flex-wrap: wrap;

  > span {
    margin: 6px 0 0;

    & + span {
      margin-left: 4px;
    }
  }
`;

const matchedResultReason = [
  '商家主动配置转人工',
  '命中场景但未命中任何策略',
  '未获取到店铺场景',
  '话术中的参数缺失',
];

// extraTag 的渲染规则参考https://leyan.yuque.com/czpqnb/yzt2rg/fthp5d49xdfr9gv6
function ExtraTag(props: ExtraTagProps) {
  const {
    message: { extra, aiExtra, llmDetail, sgpDetail, msgFromType },
    float,
  } = props;
  const extraData = useMemo(() => {
    try {
      const obj = extra ? JSON.parse(extra || '') : {};

      obj.refundStatus = (() => {
        try {
          const r = JSON.parse(obj.refundStatus);
          return Array.isArray(r) ? r : [];
        } catch (e) {
          return [];
        }
      })();
      return obj;
    } catch (e) {
      return {};
    }
  }, [extra]);

  const {
    domain,
    intent,
    resultReason,
    tradeStatus,
    tradeId,
    traceStatus,
    refundStatus,
    scenarioPackName,
    policy,
    storeGroupName,
    serviceMode,
    task,
    spuId,
    scenario,
    botMarketingType,
    enableBot,
  } = extraData;

  if (enableBot === false) {
    return (
      <TagWrapper float={float}>
        <Tag icon={<ExclamationCircleOutlined />} color="warning">
          该消息未进入AI智能接待服务
        </Tag>
      </TagWrapper>
    );
  }

  const {
    skuId,
    replyType,
    isRealReply,
    isProductQuestion,
    matchedIntent,
    matchedScenario,
    matchedPolicy,
    unRealReason,
  } = aiExtra || {};

  const line1 = !sgpDetail?.online && (
    <TagWrapper float={float}>
      {task && <Tag color="#2db7f5">任务名称: {task}</Tag>}
      {float === 'right' && botMarketingType === 'BMT_UNKNOWN' && scenario && (
        <>
          <Tooltip
            title={
              <>
                {scenarioPackName && <div>场景包名称: {scenarioPackName}</div>}
                {storeGroupName && <div>店铺配置组名称: {storeGroupName}</div>}
              </>
            }
          >
            <Tag color="magenta">场景: {scenario}</Tag>
          </Tooltip>
        </>
      )}
      {float === 'right' && scenario && policy && <Tag color="red">策略名称: {policy}</Tag>}
      {float === 'right' && serviceMode === 'NIGHT_WATCH' && policy && (
        <Tag color="red">策略名称: {policy}</Tag>
      )}
      {domain && <Tag color="volcano">类目: {domain}</Tag>}
      {float === 'left' && intent && <Tag color="orange">意图: {intent}</Tag>}
      {resultReason && <Tag color="gold">result_reason: {resultReason}</Tag>}
      {matchedResultReason.includes(resultReason) ? (
        <>
          <Tag color="lime">场景名称：{scenario}</Tag>
          <Tag color="green">策略名称: {policy}</Tag>
        </>
      ) : null}
      {tradeId ? (
        <>
          <Tooltip title={`订单号: ${tradeId}`}>
            <Tag
              color="cyan"
              style={{ cursor: 'copy' }}
              onClick={() => navigator.clipboard.writeText(tradeId)}
            >
              订单: {tradeStatusMap[tradeStatus]}
              <CopyFilled style={{ marginLeft: '4px' }} />
            </Tag>
          </Tooltip>
          {traceStatus ? (
            <Tag color="blue">物流状态: {traceStatusMap[traceStatus] || traceStatus}</Tag>
          ) : (
            <Tag color="blue">物流状态: -</Tag>
          )}
          {refundStatus?.length !== 0 ? (
            <Tag color="blue">
              退货状态:{' '}
              {refundStatus?.map((e: string) => refundStatusMap[e])?.join(',') ||
                refundStatus ||
                '-'}
            </Tag>
          ) : (
            <Tag color="blue">退货状态: -</Tag>
          )}
        </>
      ) : tradeStatus ? (
        <Tag color="blue">订单状态: {tradeStatusMap[tradeStatus]}</Tag>
      ) : (
        float === 'left' && <Tag color="blue">订单状态: 无订单</Tag>
      )}
      {serviceMode && <Tag color="geekblue">AI智能接待模式: {serviceModeMap[serviceMode]}</Tag>}
      {float === 'left' && spuId && (
        <Tag
          style={{ cursor: 'copy' }}
          color="purple"
          onClick={() => navigator.clipboard.writeText(spuId)}
        >
          SPU_ID: {spuId}
          <CopyFilled style={{ marginLeft: '4px' }} />
        </Tag>
      )}
      {skuId ? (
        <Tag
          style={{ cursor: 'copy' }}
          color="purple"
          onClick={() => navigator.clipboard.writeText(skuId)}
        >
          SKU_ID: {skuId}
          <CopyFilled style={{ marginLeft: '4px' }} />
        </Tag>
      ) : null}
    </TagWrapper>
  );

  const line2 = !sgpDetail?.online && msgFromType === 'BUYER' && (
    <TagWrapper float={float}>
      <Tag>
        最终回复类型：
        {{
          DIALOGUE: '官方场景',
          KNOWLEDGE_LLM: '商品知识',
          KNOWLEDGE_LLM_NEW: '商品知识',
          LLM: '增强版pro',
        }[replyType as string] || '其他'}
      </Tag>
      <Tag>是否回复成功：{isRealReply ? '是' : '否'}</Tag>
      {!isRealReply && aiExtra?.resultReason ? (
        <Tag>回复不成功的原因：{aiExtra?.resultReason}</Tag>
      ) : null}
      <Tag>意图：{matchedIntent}</Tag>
      <Tag>场景：{matchedScenario}</Tag>
      <Tag>策略：{matchedPolicy}</Tag>
      <Tag>
        是否商品问题：{isProductQuestion === 0 ? '否' : isProductQuestion === 1 ? '是' : '未知'}
      </Tag>
      {unRealReason ? <Tag>下发失败的原因: {unRealReason}</Tag> : null}
    </TagWrapper>
  );

  return (
    <div>
      {line1}
      {line2}
      {msgFromType === 'BUYER' && (
        <div>
          {aiExtra && !sgpDetail?.online && (
            <Button
              type="link"
              onClick={() =>
                window.parent.postMessage(
                  {
                    type: '查看AI处理明细',
                    data: aiExtra,
                  },
                  '*',
                )
              }
            >
              查看AI处理明细
              <RightOutlined />
            </Button>
          )}
          {sgpDetail?.online && (
            <Button
              type="link"
              onClick={() =>
                window.parent.postMessage(
                  {
                    type: '查看Agent处理明细',
                    data: sgpDetail.online,
                  },
                  '*',
                )
              }
            >
              查看Agent处理明细
              <RightOutlined />
            </Button>
          )}
          {llmDetail && (
            <Button
              type="link"
              onClick={() =>
                window.parent.postMessage(
                  {
                    type: '查看Prompt',
                    data: llmDetail,
                  },
                  '*',
                )
              }
            >
              查看Prompt
              <RightOutlined />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default memo(ExtraTag);
